import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { passwordRegex } from "../../Configs";
import { useLoginPlaymove, useUserApi } from "../../Hooks";
import { useTranslation } from "react-i18next";
import header from "../../assets/img/cabecalho-login.png";
import footer from "../../assets/img/footer-login.png";
import LanguageSelector from "../Shared/LanguageSelector";
import { getLocalizedRoutePath, ROUTE_KEYS } from "../../routes/definitions";
const ticksToDate = require("ticks-to-date");

export default function CriarSenhaPlaymove() {
  const navigate = useNavigate();
  const userApi = useUserApi();
  const { login } = useLoginPlaymove();
  const { t, i18n } = useTranslation();

  // Parâmetros da rota e query string
  const { forgotPasswordToken } = useParams();
  const [searchParams] = useSearchParams();

  // Estados de senha
  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");

  // Estado de expiração do link
  const [expired, setExpired] = useState(false);

  // Alerta de erro ou sucesso
  const [alert, setAlert] = useState({ type: "", message: "" });

  // Loading (ex.: ao enviar nova senha)
  const [loading, setLoading] = useState(false);

  // Seletor de idioma: lê do localStorage ou usa "pt-BR" por padrão
  const [language, setLanguage] = useState(localStorage.getItem("language") || "pt-BR");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Atualiza o idioma global ao montar o componente
  useEffect(() => {
    i18n.changeLanguage(language);
  }, [i18n, language]);

  useEffect(() => {
    const expiration = searchParams.get("expiration");
    if (!expiration) {
      setExpired(true);
      setAlert({ type: "error", message: t("resetLinkExpired") });
      return;
    }
    const expirationInTicks = parseInt(expiration, 10);
    const expirationInDate = ticksToDate(expirationInTicks);
    const now = new Date();
    if (now > expirationInDate) {
      setExpired(true);
      setAlert({ type: "error", message: t("resetLinkExpired") });
    }
  }, [searchParams, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAlert({ type: "", message: "" });

    if (expired) {
      setLoading(false);
      return;
    }
    if (password.length < 6) {
      setAlert({
        type: "error",
        message: t("passwordMinLength"),
      });
      setLoading(false);
      return;
    }
    if (!passwordRegex.test(password)) {
      setAlert({
        type: "error",
        message: t("passwordMustContainLetterAndNumber"),
      });
      setLoading(false);
      return;
    }
    if (password !== passwordAgain) {
      setAlert({
        type: "error",
        message: t("passwordsNotMatch"),
      });
      setLoading(false);
      return;
    }

    try {
      const updatePasswordResult = await userApi.updateUserPasswordByToken(
        forgotPasswordToken,
        password
      );
      if (updatePasswordResult.status === 200) {
        const responseEmail =
          updatePasswordResult?.data?.email ||
          updatePasswordResult?.data?.Email ||
          "";

        if (responseEmail) {
          const loginResult = await login(responseEmail, password, false);
          if (loginResult?.success) {
            const acessoPlataformasPath = getLocalizedRoutePath(ROUTE_KEYS.PLATFORM_ACCESS, i18n.language);
            navigate(acessoPlataformasPath);
            return;
          }
        }

        const loginPlaymovePath = getLocalizedRoutePath(ROUTE_KEYS.LOGIN_PLAYMOVE, i18n.language);
        navigate(`${loginPlaymovePath}?senhaAtualizada=true`);
      } else {
        setAlert({
          type: "error",
          message: t("resetLinkExpiredShort"),
        });
      }
    } catch (error) {
      console.error(error);
      setAlert({
        type: "error",
        message: t("errorResettingPassword"),
      });
    }
    setLoading(false);
  };

  return (
    <div className={`min-h-screen flex flex-col bg-white ${loading ? "opacity-70 cursor-wait" : ""}`}>
      {/* HEADER: Em mobile, altura natural; em desktop, 15% da tela */}
      <div className="w-full flex-shrink-0 h-auto md:h-[15%]">
        <img
          src={header}
          alt={t("headerAlt")}
          className="w-full h-auto md:h-full object-contain md:object-cover"
          style={{ maxWidth: "100%" }}
        />
      </div>

      {/* CONTEÚDO: Em mobile, altura natural; em desktop, 65% da tela */}
      <div className="flex-grow flex flex-col items-center px-4 pt-8">
        <h1 className="text-2xl font-bold text-gray-800 text-center">
          {t("createPasswordTitle")}
        </h1>
        <p className="text-gray-600 text-center mt-2">
          {t("createPasswordSubtitle")}
        </p>

        {/* Card (fundo cinza-claro) */}
        <div className="bg-[#F5F9FF] w-full max-w-md mt-8 p-6 rounded shadow">
          <form onSubmit={handleSubmit}>
            <label htmlFor="password" className="block font-semibold text-gray-700">
              {t("passwordLabel")}
            </label>
            <input
              id="password"
              type="password"
              className="w-full mt-1 mb-4 p-2 border rounded border-gray-300 focus:outline-none"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={expired}
            />

            <label htmlFor="passwordAgain" className="block font-semibold text-gray-700">
              {t("passwordAgainLabel")}
            </label>
            <input
              id="passwordAgain"
              type="password"
              className="w-full mt-1 mb-4 p-2 border rounded border-gray-300 focus:outline-none"
              value={passwordAgain}
              onChange={(e) => setPasswordAgain(e.target.value)}
              required
              disabled={expired}
            />

            {alert.type && (
              <div className={`text-sm p-2 rounded mb-2 ${
                alert.type === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
              }`}>
                {alert.message}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || expired}
              className={`w-full py-3 rounded-full bg-blue-600 text-white text-lg font-bold transition-all ${
                loading || expired ? "opacity-70 cursor-not-allowed" : "hover:bg-blue-700"
              }`}
            >
              {loading ? t("sending") : t("confirm")}
            </button>
          </form>
        </div>
      </div>

      {/* FOOTER: Em mobile, altura natural; em desktop, 10% da tela */}
      <div className="w-full flex-shrink-0 h-auto md:h-[10%]">
        <div className="flex justify-center items-center">
          <img src={footer} alt={t("footerAlt")} />
        </div>
      </div>

      {/* Seletor de Idioma (canto inferior direito) */}
      <LanguageSelector onLanguageChange={(lang) => setLanguage(lang)} />
    </div>
  );
}
